import React, { useState } from "react";
import {
    Button,
    CircularProgress,
    DialogActions,
    DialogContent,
    DialogTitle,
    TextField,
} from "@mui/material";
import CheckOutlinedIcon from "@mui/icons-material/CheckOutlined";
import PostData from "../../services/PostData";
import SnackbarEx from "../Helpers/SnackbarEx";
import {
    catalogPrimaryButtonSx,
    catalogQuietButtonSx,
} from "../Products/catalogGridStyles.js";

export default function ConfirmUploadRecord({ recordData, closeDialog }) {
    const [submitLoad, setSubmitLoad] = useState(false);
    const [successUpload, setSuccessUpload] = useState(false);
    const [openTestSnack, setOpenTestSnack] = useState({
        color: "",
        message: "",
        open: false,
    });

    const [itemData, setItemData] = useState([
        { field: "SKU", data: recordData?.sku ?? "", name: "sku", required: true },
        {
            field: "Item Name",
            data: recordData?.fields?.["Item Name"] ?? "",
            name: "item_name",
            required: true,
        },
        {
            field: "UPC",
            data: recordData?.fields?.UPC?.text ?? "",
            name: "upc",
            required: true,
        },
        {
            field: "WPID",
            data: recordData?.fields?.WPID ?? "",
            name: "wpid",
            required: true,
        },
        {
            field: "Listing Field",
            data: recordData?.fields?.["Listing Field"] ?? "",
            name: "listing_field",
            required: true,
        },
        {
            field: "Listing Price",
            data: "",
            name: "listing_price",
            helper: "Price sent with the Walmart item feed",
            required: true,
        },
    ]);
    const [errors, setErrors] = useState({});

    const handleInputChanges = (e, fieldName) => {
        const { value } = e.target;
        setItemData((prevVals) =>
            prevVals.map((val) => (val.name === fieldName ? { ...val, data: value } : val))
        );
        if (value) {
            setErrors((prevErrors) => ({ ...prevErrors, [fieldName]: "" }));
        }
    };

    const handleSubmit = async () => {
        setSubmitLoad(true);
        const newErrors = {};
        let hasError = false;

        itemData.forEach((val) => {
            if (val.required && !val.data) {
                newErrors[val.name] = `${val.field} cannot be blank`;
                hasError = true;
            }
        });

        if (hasError) {
            setErrors(newErrors);
            setSubmitLoad(false);
            return;
        }

        const { error } = await PostData(itemData, "broylesllc/create-walmart-sku", null);
        if (error) {
            setSubmitLoad(false);
            setOpenTestSnack({
                color: "error",
                message: "An error has occurred.",
                open: true,
            });
            return;
        }
        setSuccessUpload(true);
        setTimeout(() => closeDialog("success"), 900);
    };

    return (
        <>
            <DialogTitle
                sx={{
                    fontWeight: 700,
                    letterSpacing: "-0.02em",
                    color: "#1c2430",
                    pb: 1,
                }}
            >
                Confirm Walmart upload
            </DialogTitle>
            <DialogContent dividers sx={{ px: 3, py: 2.5 }}>
                <div className="d-flex flex-column gap-3">
                    {itemData.map((val) => (
                        <TextField
                            key={val.name}
                            label={val.field}
                            name={val.name}
                            value={val.data}
                            onChange={(e) => handleInputChanges(e, val.name)}
                            fullWidth
                            error={!!errors[val.name]}
                            helperText={errors[val.name] ?? val?.helper}
                            sx={{
                                "& .MuiOutlinedInput-root": {
                                    borderRadius: "10px",
                                    backgroundColor: "#ffffff",
                                    fontSize: 14,
                                    "& fieldset": {
                                        borderColor: "#94a3b8",
                                        borderWidth: 1,
                                    },
                                    "&:hover fieldset": {
                                        borderColor: "#64748b",
                                    },
                                    "&.Mui-focused fieldset": {
                                        borderColor: "#0d9488",
                                        borderWidth: 1.5,
                                    },
                                },
                                "& .MuiOutlinedInput-input": {
                                    py: "12px",
                                },
                                "& .MuiFormHelperText-root": {
                                    mx: 0.5,
                                    mt: 0.75,
                                },
                            }}
                        />
                    ))}
                </div>
            </DialogContent>
            <DialogActions sx={{ px: 3, py: 2, gap: 1 }}>
                <Button onClick={() => closeDialog()} sx={catalogQuietButtonSx}>
                    Cancel
                </Button>
                <Button
                    variant="contained"
                    disabled={submitLoad || successUpload}
                    onClick={handleSubmit}
                    startIcon={
                        successUpload ? (
                            <CheckOutlinedIcon fontSize="small" />
                        ) : submitLoad ? (
                            <CircularProgress size={14} color="inherit" />
                        ) : null
                    }
                    sx={{
                        ...catalogPrimaryButtonSx,
                        ...(successUpload
                            ? {
                                  backgroundColor: "#1fa971",
                                  "&:hover": { backgroundColor: "#1fa971" },
                              }
                            : {}),
                    }}
                >
                    {successUpload ? "Listed" : "Upload to Walmart"}
                </Button>
            </DialogActions>
            {openTestSnack.open && (
                <SnackbarEx
                    color={openTestSnack.color}
                    message={openTestSnack.message}
                    open={openTestSnack.open}
                    close={() =>
                        setOpenTestSnack({ color: "", message: "", open: false })
                    }
                />
            )}
        </>
    );
}
